import React, { useEffect } from 'react';
import styles from '../styles/dialog.module.css';
import { resolveMessage } from '../i18n/translate';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'confirm' | 'alert';
  variant?: 'danger' | 'warning' | 'info' | 'success';
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  type = 'confirm',
  variant = 'info'
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const confirmLabel = confirmText ?? resolveMessage(type === 'alert' ? 'dialog.ok' : 'dialog.yes');
  const cancelLabel = cancelText ?? resolveMessage('dialog.no');

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={`${styles.dialog} ${styles[variant]}`}>
        {title && (
          <div className={styles.header}>
            <h3 className={styles.title}>{title}</h3>
          </div>
        )}
        <div className={styles.content}>
          <p className={styles.message}>{message}</p>
        </div>
        <div className={styles.actions}>
          {type === 'alert' ? (
            <button 
              className={`${styles.button} ${styles.primary}`}
              onClick={onClose}
            >
              {confirmLabel}
            </button>
          ) : (
            <>
              <button 
                className={`${styles.button} ${styles.secondary}`}
                onClick={onClose}
              >
                {cancelLabel}
              </button>
              <button 
                className={`${styles.button} ${styles.primary} ${variant === 'danger' ? styles.danger : ''}`}
                onClick={handleConfirm}
              >
                {confirmLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog; 